// Single source of truth for cached auth state.
// Tokens live in a switchable backend: localStorage when "Remember me" is on (survives a browser
// restart) or sessionStorage when off (cleared on browser close). The user display cache stays in
// localStorage (non-sensitive; cleared on logout / when restore finds no session token).
// One namespaced key per BP (wp.auth.bp.<bpId>) so concurrent cross-BP writes from different tabs
// don't clobber each other, and cleanup is safe via the wp.auth. prefix.
const BP_PREFIX = 'wp.auth.bp.'; // wp.auth.bp.<bpId> = { accessToken, refreshToken, scopes, expiresAt }
const USER_KEY = 'wp.auth.user';
const SESSION_KEY = 'wp.auth.session'; // { accessToken, refreshToken, expiresAt } — pre-profile identity token
const NS_PREFIX = 'wp.auth.';

// Active token backend. Defaults to localStorage; switched by setRemember()/resolveStore().
let tokenStore = localStorage;

const readFrom = (store, k) => {
  try {
    return JSON.parse(store.getItem(k));
  } catch {
    return null;
  }
};
const read = (k) => readFrom(tokenStore, k);
const write = (k, v) => tokenStore.setItem(k, JSON.stringify(v));

// localStorage-only helpers for the non-sensitive user display cache.
const readUser = (k) => {
  try {
    return JSON.parse(localStorage.getItem(k));
  } catch {
    return null;
  }
};

// The legacy flat keys (accessToken_<bpId> etc.) are no longer written — all readers go through
// the store API (authStore.getAppToken / getAppScopes). This only removes any that linger from a
// previous version, on logout and on first migration.
const removeLegacyFlat = (store, bpId) => {
  ['accessToken', 'refreshToken', 'scopes'].forEach((p) => store.removeItem(`${p}_${bpId}`));
};

// The single primitive for fully deleting one BP's entry from a backend — the canonical
// namespaced key plus any lingering legacy flat keys. Every removal path goes through it.
const removeBPFrom = (store, bpId) => {
  removeLegacyFlat(store, bpId);
  store.removeItem(`${BP_PREFIX}${bpId}`);
};

// Drop the session + per-BP token keys from a given storage backend.
const clearTokenKeys = (store) => {
  store.removeItem(SESSION_KEY);
  Object.keys(store)
    .filter((k) => k.startsWith(BP_PREFIX))
    .forEach((k) => removeBPFrom(store, k.slice(BP_PREFIX.length)));
};

export const tokenStorage = {
  // Choose where tokens are persisted: localStorage (remember) or sessionStorage (ephemeral).
  // Clears the OTHER backend so only one holds the live session.
  setRemember(remember) {
    tokenStore = remember ? localStorage : sessionStorage;
    clearTokenKeys(remember ? sessionStorage : localStorage);
  },

  // On startup, point at whichever backend currently holds the session token (sessionStorage wins
  // when present — that's an ephemeral session being restored within the same browser session).
  resolveStore() {
    tokenStore = sessionStorage.getItem(SESSION_KEY) ? sessionStorage : localStorage;
  },

  getCachedUser: () => readUser(USER_KEY) ?? readUser('user'),
  cacheUser: (u) => {
    localStorage.setItem(USER_KEY, JSON.stringify(u));
    localStorage.setItem('user', JSON.stringify(u)); // legacy compat: App.vue / settings read 'user'
  },
  clearCachedUser: () => {
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem('user');
  },

  // Pre-profile identity token — the durable home for the backend-issued session token.
  loadSession: () => read(SESSION_KEY),
  persistSession: (entry) => write(SESSION_KEY, entry),
  clearSession: () => tokenStore.removeItem(SESSION_KEY),

  loadBPToken: (bpId) => read(`${BP_PREFIX}${bpId}`),
  persistBPTokens(bpId, entry) {
    write(`${BP_PREFIX}${bpId}`, entry); // canonical (has expiresAt); flat keys no longer written
  },

  // Remove a single BP's token entry (canonical + any legacy flat keys) from the active backend.
  removeBPToken(bpId) {
    removeBPFrom(tokenStore, bpId);
  },

  loadAllBPTokens() {
    const map = new Map();
    for (const k of Object.keys(tokenStore)) {
      if (k.startsWith(BP_PREFIX)) map.set(k.slice(BP_PREFIX.length), read(k));
    }
    return map;
  },

  clearAllBPTokens() {
    Object.keys(tokenStore)
      .filter((k) => k.startsWith(BP_PREFIX))
      .forEach((k) => removeBPFrom(tokenStore, k.slice(BP_PREFIX.length)));
  },

  // Wipe on logout: our namespace + legacy flat per-BP keys + cached user/abilities, from BOTH
  // backends (so an ephemeral session can't leave anything behind, and vice versa).
  clearAll() {
    [localStorage, sessionStorage].forEach((store) => {
      Object.keys(store)
        .filter(
          (k) =>
            k.startsWith(NS_PREFIX) ||
            /^(accessToken|refreshToken|scopes)_/.test(k) ||
            k === 'user' ||
            k === 'userAbilities',
        )
        .forEach((k) => store.removeItem(k));
    });
  },

  // One-time seed of the canonical namespaced entries (wp.auth.bp.<bpId>) from any pre-existing
  // legacy flat keys, so refresh() has a refreshToken to work with after a deploy that predates
  // the namespaced format. The seeded entry has no per-BP expiresAt → treated as expired →
  // refreshed on first use (the stored refresh token allows a silent re-fetch, not a logout).
  // Runs once in installAuth before hydrate(); idempotent once everything is on the new format.
  migrateLegacy() {
    for (const k of Object.keys(tokenStore)) {
      const m = k.match(/^(accessToken|refreshToken|scopes)_(.+)$/);
      if (!m) continue;
      const bpId = m[2];
      if (read(`${BP_PREFIX}${bpId}`)) continue; // already have a canonical entry
      const cur = {};
      cur.accessToken = read(`accessToken_${bpId}`);
      cur.refreshToken = read(`refreshToken_${bpId}`);
      cur.scopes = read(`scopes_${bpId}`) ?? [];
      write(`${BP_PREFIX}${bpId}`, cur);
    }
    // Drop the legacy Amplify mirror keys that are no longer written after the backend-owned migration.
    ['oauth2AccessToken', 'oauth2IdToken', 'last-auth-user', 'accessExpiry'].forEach((k) =>
      tokenStore.removeItem(k),
    );
  },
};
